import { useEffect, useState } from "react";

/* Everything the UI needs to offer "install this app".
 *
 * The awkward part is that only Chromium fires beforeinstallprompt. iOS Safari
 * never does and never will, so a button there would be a dead end. The hook
 * reports the platform so the page can show real instructions instead of a
 * control that does nothing.
 */

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;

  // iPadOS 13+ reports itself as a Mac; touch points are what give it away.
  const iPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || iPadOS) return "ios";

  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

function detectInstalled() {
  if (typeof window === "undefined") return false;
  const standalone =
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return Boolean(standalone || window.navigator.standalone);
}

export default function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(detectInstalled);
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    function onBeforeInstall(e) {
      // Suppress the mini-infobar so the page owns the moment.
      e.preventDefault();
      setDeferred(e);
    }
    function onInstalled() {
      setDeferred(null);
      setInstalled(true);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Launching from the home screen does not re-fire anything, so watch the
    // display mode as well.
    const mq = window.matchMedia("(display-mode: standalone)");
    const onModeChange = (e) => e.matches && setInstalled(true);
    if (mq.addEventListener) mq.addEventListener("change", onModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (mq.removeEventListener) mq.removeEventListener("change", onModeChange);
    };
  }, []);

  async function promptInstall() {
    if (!deferred) return "unavailable";
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // The event is single use whatever the answer.
    setDeferred(null);
    return outcome; // "accepted" | "dismissed"
  }

  return {
    platform,
    installed,
    canPrompt: Boolean(deferred),
    promptInstall,
  };
}
